import { FormikProps } from "formik";
import {
  DiscountTarget,
  PromotionType,
} from "../../../interfaces/promotionType";
import CustomInput from "../../../components/common/Inputs/CustomInput";
import SelectInput, {
  SelectInputProps,
} from "../../../components/common/Inputs/SelectInput";
import CustomDatePicker from "../../../components/common/Inputs/CustomDatePicket";
import classNames from "classnames";
import { MultiSelect } from "../../../components/common/Inputs/multi-select/MultiSelect";
import { useEffect, useMemo } from "react";
import Tabs from "../../../components/common/Tabs/TabContainer";
import Tab from "../../../components/common/Tabs/Tab";
import { PromotionTargetsForm } from "./PromotionTargetsForm";
import { PromotionProductTable } from "./PromotionTargetsTable";
import { useProductsByCategoriesQuery } from "../../products/queries/useProductsQuery";
import { useDistributorTypesOptions } from "../../distributortypes/hooks/useDistributorTypesOptions";

type PromotionFormProps = {
  values?: PromotionType;
  formik: FormikProps<PromotionType>;
};

const objetivoDescuentoOptions: SelectInputProps["options"] = [
  { label: "Cualquier Producto", value: "all" },
  { label: "Producto Específico", value: "product" },
  { label: "Categoría de Producto", value: "product-category" },
];

const applyByOptions: SelectInputProps["options"] = [
  { label: "Descuento por producto o categoría", value: "product-category" },
  { label: "Descuento por volumen de producto", value: "volume" },
  { label: "Descuento por monto total del pedido", value: "amount" },
];

const PromotionTargetsFormWrapper = (
  props: PromotionFormProps & { discountTarget: DiscountTarget },
) => {
  const { formik, discountTarget } = props;
  const { distributorTypesOptions } = useDistributorTypesOptions();
  const datePickerClass = {
    gridTemplateColumns: classNames(
      "grid grid-cols-1 md:grid-cols-2 gap-4 items-end",
    ),
  };

  const applyBy = useMemo(() => {
    return formik.values.applyBy;
  }, [formik.values.applyBy]);

  useEffect(() => {
    if (applyBy !== "volume" && formik.values.volume) {
      formik.setFieldValue("volume", undefined);
    }
    if (applyBy !== "amount" && formik.values.amount) {
      formik.setFieldValue("amount", undefined);
    }
  }, [applyBy]);

  useEffect(() => {
    if (discountTarget === "all" && formik.values.targetItemsId) {
      formik.setFieldValue("targetItemsId", []);
    }
  }, [discountTarget]);

  return (
    <div>
      <div className={datePickerClass.gridTemplateColumns}>
        <CustomInput
          label="Nombre de la Promoción"
          error={
            formik.touched.promotionName &&
            formik.errors.promotionName &&
            Array.isArray(formik.errors.promotionName)
              ? formik.errors.promotionName.join(", ")
              : formik.errors.promotionName
          }
          {...formik.getFieldProps("promotionName")}
          onChange={(e) => {
            formik.handleChange(e);
          }}
        />
        <MultiSelect
          {...formik.getFieldProps("distributorTypeIds")}
          label="Tipo de distribuidor al que se aplicará la promoción"
          options={distributorTypesOptions}
          selectedOptions={formik.values.distributorTypeIds}
          onChange={(value) => {
            formik.setFieldValue("distributorTypeIds", value);
          }}
          error={
            formik.touched.distributorTypeIds &&
            formik.errors.distributorTypeIds &&
            Array.isArray(formik.errors.distributorTypeIds)
              ? formik.errors.distributorTypeIds.join(", ")
              : formik.errors.distributorTypeIds
          }
        />
        <CustomDatePicker
          label="Fecha de Inicio de la Promoción"
          {...formik.getFieldProps("promotionStartAt")}
          handleChange={(date) => {
            formik.setFieldValue("promotionStartAt", date);
          }}
          error={
            formik.touched.promotionStartAt &&
            formik.errors.promotionStartAt &&
            Array.isArray(formik.errors.promotionStartAt)
              ? formik.errors.promotionStartAt.join(", ")
              : formik.errors.promotionStartAt
          }
        />
        <CustomDatePicker
          label="Fecha de Fin de la Promoción"
          {...formik.getFieldProps("promotionEndsAt")}
          handleChange={(date) => {
            formik.setFieldValue("promotionEndsAt", date);
          }}
          error={
            formik.touched.promotionEndsAt &&
            formik.errors.promotionEndsAt &&
            Array.isArray(formik.errors.promotionEndsAt)
              ? formik.errors.promotionEndsAt.join(", ")
              : formik.errors.promotionEndsAt
          }
        />
        <SelectInput
          label="Tipo de Descuento"
          options={applyByOptions}
          {...formik.getFieldProps("applyBy")}
          onChange={(e) => {
            formik.handleChange(e);
          }}
          error={
            formik.touched.applyBy &&
            formik.errors.applyBy &&
            Array.isArray(formik.errors.applyBy)
              ? formik.errors.applyBy.join(", ")
              : formik.errors.applyBy
          }
        />
        <SelectInput
          label="Objetivo del Descuento"
          options={objetivoDescuentoOptions}
          {...formik.getFieldProps("discountTarget")}
          onChange={(e) => {
            formik.handleChange(e);
          }}
          error={
            formik.touched.discountTarget &&
            formik.errors.discountTarget &&
            Array.isArray(formik.errors.discountTarget)
              ? formik.errors.discountTarget.join(", ")
              : formik.errors.discountTarget
          }
        />
      </div>
      <div className={datePickerClass.gridTemplateColumns}>
        <CustomInput
          label="Porcentaje de Descuento"
          type="number"
          max={100}
          error={
            formik.touched.discountPercentage &&
            formik.errors.discountPercentage &&
            Array.isArray(formik.errors.discountPercentage)
              ? formik.errors.discountPercentage.join(", ")
              : formik.errors.discountPercentage
          }
          {...formik.getFieldProps("discountPercentage")}
          onChange={(e) => {
            if (parseFloat(e.target.value) > 100) {
              formik.setFieldValue("discountPercentage", 100);
              return;
            }
            if (parseFloat(e.target.value) < 0) {
              formik.setFieldValue("discountPercentage", "");
              return;
            }
            formik.handleChange(e);
          }}
        />
        {applyBy === "volume" && (
          <CustomInput
            label="Cantidad Mínima de Productos para aplicar el Descuento"
            type="number"
            error={
              formik.touched.volume &&
              formik.errors.volume &&
              Array.isArray(formik.errors.volume)
                ? formik.errors.volume.join(", ")
                : formik.errors.volume
            }
            {...formik.getFieldProps("volume")}
            onChange={(e) => {
              formik.handleChange(e);
            }}
          />
        )}
        {applyBy === "amount" && (
          <CustomInput
            label="Monto Mínimo del Pedido para aplicar el Descuento"
            type="number"
            error={
              formik.touched.amount &&
              formik.errors.amount &&
              Array.isArray(formik.errors.amount)
                ? formik.errors.amount.join(", ")
                : formik.errors.amount
            }
            {...formik.getFieldProps("amount")}
            onChange={(e) => {
              formik.handleChange(e);
            }}
          />
        )}
      </div>
      {discountTarget !== "all" && (
        <PromotionTargetsForm formik={formik} discountTarget={discountTarget} />
      )}
    </div>
  );
};

export const PromotionForm = (props: PromotionFormProps) => {
  const { formik } = props;
  const { discountTarget, targetItemsId } = formik.values;

  const { data: productsData } = useProductsByCategoriesQuery({
    categories: targetItemsId || [],
    enabled: Boolean(
      discountTarget === "product-category" && targetItemsId?.length,
    ),
  });

  if (discountTarget === "product-category") {
    const targetItemsId = formik.values.targetItemsId || [];
    const discountPercentage = formik.values.discountPercentage || 0;
    return (
      <Tabs>
        <Tab title="Datos Generales">
          <PromotionTargetsFormWrapper
            {...props}
            discountTarget={discountTarget}
          />
        </Tab>
        <Tab title="Descuentos por Producto">
          <PromotionProductTable
            products={productsData?.data || []}
            targetItemsId={targetItemsId}
            discountPercentage={discountPercentage}
            isEditable={false}
            shouldDisplayProductsByList
          />
        </Tab>
      </Tabs>
    );
  }

  return (
    <PromotionTargetsFormWrapper {...props} discountTarget={discountTarget} />
  );
};
